import React from "react";
import { IoMdMore } from "react-icons/io";
import { Menu } from "@headlessui/react";
import { OnboardingData, Milestone } from "../../lib/types";
import { Inbox } from "../../lib/inboxLib";
import CustomTooltip from "../Tooltip";
import AccountSetupTracker from "../accountSetupTracker";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { bootstrap, getHealth } from "../../lib/inboxLib";

interface InboxCardsProps {
  inboxesWithOnboarding: (Inbox & { onboarding: OnboardingData })[];
  healthStatuses: Record<
    string,
    { status: "healthy" | "unhealthy" | "loading" | "atRisk"; message: string }
  >;
  setHealthStatuses: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        {
          status: "healthy" | "unhealthy" | "loading" | "atRisk";
          message: string;
        }
      >
    >
  >;
  userId?: string;
  isAdmin?: boolean;
}

const InboxCards: React.FC<InboxCardsProps> = ({
  inboxesWithOnboarding,
  healthStatuses,
  setHealthStatuses,
  userId,
  isAdmin,
}) => {
  const { showSnackbar } = useSnackbar();

  return (
    <div className="order-1 lg:order-none flex flex-col gap-6">
      {inboxesWithOnboarding.length > 0 ? (
        inboxesWithOnboarding.map(
          (inbox: Inbox & { onboarding: OnboardingData }) => (
            <div
              key={inbox.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4"
            >
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center min-w-0">
                    <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xl font-semibold mr-4 flex-shrink-0">
                      {inbox.profilePicture ? (
                        <img
                          src={inbox.profilePicture}
                          alt={inbox.displayName || inbox.email}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        (inbox.displayName || inbox.email || "?").charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0 pr-2">
                      <h3 className="text-lg font-semibold truncate">
                        {inbox.displayName || inbox.email}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 truncate">
                        {inbox.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Check if onboarding is active (any milestone in progress) */}
                    {(() => {
                      const isOnboardingActive =
                        inbox.onboarding?.milestones?.some(
                          (milestone: Milestone) =>
                            milestone.status === "in_progress"
                        );

                      if (isOnboardingActive) {
                        return (
                          <div>
                            <div className="hidden lg:flex items-center gap-2 pl-3 pr-6 py-1 rounded-full text-sm max-w-[140px] bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              <div className="w-2 h-2 rounded-full flex-shrink-0 bg-blue-500" />
                              <span className="break-words text-center">
                                Syncing...
                              </span>
                            </div>
                            <div className="lg:hidden">
                              <div className="w-3 h-3 rounded-full bg-blue-500" />
                            </div>
                          </div>
                        );
                      }

                      return healthStatuses[inbox.id]?.message ? (
                        <CustomTooltip
                          text={
                            <div>
                              <div className="font-semibold mb-1 text-center capitalize">
                                {healthStatuses[inbox.id]?.status === "healthy"
                                  ? "Healthy and Scanning"
                                  : healthStatuses[inbox.id]?.status ===
                                      "atRisk"
                                    ? "At Risk"
                                    : healthStatuses[inbox.id]?.status}
                              </div>
                              {healthStatuses[inbox.id]?.message}
                            </div>
                          }
                          className="w-64 text-left"
                        >
                          <div>
                            <div
                              className={`hidden lg:flex items-center gap-2 pl-3 pr-6 py-1 rounded-full text-sm max-w-[140px] cursor-help ${
                                healthStatuses[inbox.id]?.status === "healthy"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                  : healthStatuses[inbox.id]?.status ===
                                      "unhealthy"
                                    ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    : healthStatuses[inbox.id]?.status ===
                                        "atRisk"
                                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                              }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  healthStatuses[inbox.id]?.status === "healthy"
                                    ? "bg-green-500"
                                    : healthStatuses[inbox.id]?.status ===
                                        "unhealthy"
                                      ? "bg-red-500"
                                      : healthStatuses[inbox.id]?.status ===
                                          "atRisk"
                                        ? "bg-yellow-500"
                                        : "bg-gray-400"
                                }`}
                              />
                              <span className="break-words text-center">
                                {healthStatuses[inbox.id]?.status === "healthy"
                                  ? "Healthy and Scanning"
                                  : healthStatuses[inbox.id]?.status ===
                                      "unhealthy"
                                    ? "Unhealthy"
                                    : healthStatuses[inbox.id]?.status ===
                                        "atRisk"
                                      ? "At Risk"
                                      : "Checking..."}
                              </span>
                            </div>
                            <div className="lg:hidden cursor-help">
                              <div
                                className={`w-3 h-3 rounded-full ${
                                  healthStatuses[inbox.id]?.status === "healthy"
                                    ? "bg-green-500"
                                    : healthStatuses[inbox.id]?.status ===
                                        "unhealthy"
                                      ? "bg-red-500"
                                      : healthStatuses[inbox.id]?.status ===
                                          "atRisk"
                                        ? "bg-yellow-500"
                                        : "bg-gray-400"
                                }`}
                              />
                            </div>
                          </div>
                        </CustomTooltip>
                      ) : (
                        <>
                          <div
                            className={`hidden lg:flex items-center gap-2 pl-3 pr-6 py-1 rounded-full text-sm max-w-[140px] ${
                              healthStatuses[inbox.id]?.status === "healthy"
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : healthStatuses[inbox.id]?.status ===
                                    "unhealthy"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                  : healthStatuses[inbox.id]?.status ===
                                      "atRisk"
                                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            }`}
                          >
                            <div
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                healthStatuses[inbox.id]?.status === "healthy"
                                  ? "bg-green-500"
                                  : healthStatuses[inbox.id]?.status ===
                                      "unhealthy"
                                    ? "bg-red-500"
                                    : healthStatuses[inbox.id]?.status ===
                                        "atRisk"
                                      ? "bg-yellow-500"
                                      : "bg-gray-400"
                              }`}
                            />
                            <span className="break-words text-center">
                              {healthStatuses[inbox.id]?.status === "healthy"
                                ? "Healthy and Scanning"
                                : healthStatuses[inbox.id]?.status ===
                                    "unhealthy"
                                  ? "Unhealthy"
                                  : healthStatuses[inbox.id]?.status ===
                                      "atRisk"
                                    ? "At Risk"
                                    : "Checking..."}
                            </span>
                          </div>
                          <div className="lg:hidden">
                            <div
                              className={`w-3 h-3 rounded-full ${
                                healthStatuses[inbox.id]?.status === "healthy"
                                  ? "bg-green-500"
                                  : healthStatuses[inbox.id]?.status ===
                                      "unhealthy"
                                    ? "bg-red-500"
                                    : healthStatuses[inbox.id]?.status ===
                                        "atRisk"
                                      ? "bg-yellow-500"
                                      : "bg-gray-400"
                              }`}
                            />
                          </div>
                        </>
                      );
                    })()}

                    <Menu as="div" className="relative">
                      <Menu.Button className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600">
                        <IoMdMore className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </Menu.Button>
                      <Menu.Items className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-700 rounded-md shadow-lg z-10">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? "bg-gray-100 dark:bg-gray-600" : ""
                              } flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                              onClick={async () => {
                                try {
                                  await bootstrap(inbox.id);
                                  showSnackbar(
                                    "Account setup bootstrapping initiated",
                                    "success"
                                  );
                                  // Refresh health status after bootstrapping
                                  const health = await getHealth(inbox.id);
                                  setHealthStatuses((prev) => ({
                                    ...prev,
                                    [inbox.id]: health,
                                  }));
                                } catch (error) {
                                  console.error(
                                    "Failed to bootstrap inbox:",
                                    error
                                  );
                                  showSnackbar(
                                    "Failed to initiate bootstrapping",
                                    "error"
                                  );
                                }
                              }}
                            >
                              Re-run Account Setup
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href={
                                isAdmin
                                  ? `/settings/inbox/${inbox.email}?userId=${userId}`
                                  : `/settings/inbox/${inbox.email}`
                              }
                              className={`${
                                active ? "bg-gray-100 dark:bg-gray-600" : ""
                              } flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                            >
                              Inbox Settings
                            </a>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Menu>
                  </div>
                </div>

                {inbox.onboarding?.milestones?.length > 0 && (
                  <div className="mb-4">
                    <AccountSetupTracker
                      milestones={inbox.onboarding.milestones}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        )
      ) : (
        <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded">
          No inboxes found. Please set up an inbox to get started.
        </div>
      )}
    </div>
  );
};

export default InboxCards;
